import type { BeadColor } from './types';

/**
 * Bead colour libraries.
 *
 * IMPORTANT: the Delica and Toho screen colours here are approximations
 * intended for designing on screen. Finishes (matte, silver-lined, AB, luster)
 * do not survive being flattened to a single hex value, and no two monitors
 * agree anyway. Always check a physical colour card before ordering beads.
 *
 * Users can load an accurate library of their own via Palette -> Import CSV
 * (`code,name,hex,finish` per line).
 */

export interface BeadLibrary {
  id: string;
  label: string;
  brand: string;
  note?: string;
  colors: BeadColor[];
}

type Row = [code: string, name: string, hex: string, finish?: string];

function build(brand: string, prefix: string, rows: Row[]): BeadColor[] {
  return rows.map(([code, name, hex, finish]) => ({
    id: `${prefix}-${code}`,
    code,
    name,
    hex,
    brand,
    finish,
  }));
}

const DELICA_ROWS: Row[] = [
  // Neutrals
  ['DB0010', 'Opaque Black', '#141414', 'Opaque'],
  ['DB0310', 'Matte Black', '#282828', 'Matte'],
  ['DB0200', 'Opaque Chalk White', '#f7f4ec', 'Opaque'],
  ['DB0351', 'Matte Opaque White', '#f1ede3', 'Matte'],
  ['DB0201', 'White Pearl', '#f3eee2', 'Ceylon'],
  ['DB0202', 'White Pearl Luster', '#eae3d5', 'Luster'],
  ['DB0203', 'Cream Ceylon', '#f4e7cd', 'Ceylon'],
  ['DB0731', 'Opaque Light Grey', '#b9b8b3', 'Opaque'],
  ['DB0732', 'Opaque Grey', '#8d8d8b', 'Opaque'],
  ['DB0733', 'Opaque Dark Grey', '#5c5c5c', 'Opaque'],
  ['DB0306', 'Matte Metallic Slate', '#4b4f54', 'Matte metallic'],
  ['DB0021', 'Nickel Plated', '#a8abb0', 'Plated'],
  ['DB0035', 'Galvanized Silver', '#c7cacf', 'Galvanized'],
  ['DB0041', 'Silver Lined Crystal', '#e6edf1', 'Silver-lined'],
  ['DB0114', 'Transparent Grey Luster', '#9aa0a4', 'Luster'],

  // Golds, bronzes, coppers
  ['DB0031', '24kt Gold Plated', '#d6ab51', 'Plated'],
  ['DB0034', 'Galvanized Gold', '#cda44d', 'Galvanized'],
  ['DB0410', 'Galvanized Yellow Gold', '#c9963c', 'Galvanized'],
  ['DB0040', 'Copper Plated', '#b06a3d', 'Plated'],
  ['DB0022', 'Metallic Bronze', '#8a6a42', 'Metallic'],
  ['DB0011', 'Metallic Steel', '#67707a', 'Metallic'],
  ['DB0042', 'Silver Lined Gold', '#e0b451', 'Silver-lined'],
  ['DB0331', 'Matte 24kt Gold', '#c3a05a', 'Matte plated'],
  ['DB0321', 'Matte Copper', '#9c6242', 'Matte metallic'],

  // Yellows / oranges
  ['DB0721', 'Opaque Yellow', '#f2c62a', 'Opaque'],
  ['DB0751', 'Matte Opaque Yellow', '#e8c245', 'Matte'],
  ['DB0233', 'Lined Crystal Cream', '#f6ecc8', 'Lined'],
  ['DB0233b', 'Pale Butter', '#f7e9a8', 'Lined'],
  ['DB0722', 'Opaque Squash', '#eda42a', 'Opaque'],
  ['DB0651', 'Opaque Tangerine', '#ea7a24', 'Opaque'],
  ['DB0752', 'Matte Opaque Orange', '#e08237', 'Matte'],
  ['DB0703', 'Transparent Amber', '#d99432', 'Transparent'],
  ['DB0681', 'Silver Lined Amber', '#e8a43a', 'Silver-lined'],
  ['DB0207', 'Salmon Ceylon', '#f0c3ad', 'Ceylon'],
  ['DB0206', 'Peach Ceylon', '#f4d6ba', 'Ceylon'],

  // Reds / pinks
  ['DB0723', 'Opaque Red', '#c9302c', 'Opaque'],
  ['DB0753', 'Matte Opaque Red', '#bd3a33', 'Matte'],
  ['DB0602', 'Silver Lined Red', '#cf2b3c', 'Silver-lined'],
  ['DB0774', 'Matte Transparent Ruby', '#8f2333', 'Matte transparent'],
  ['DB0654', 'Opaque Dark Cranberry', '#8c2740', 'Opaque'],
  ['DB0874', 'Matte Transparent Wine', '#6d2233', 'Matte transparent'],
  ['DB0210', 'Pink Ceylon', '#f2c2ce', 'Ceylon'],
  ['DB0245', 'Lined Crystal Pink', '#eda9bd', 'Lined'],
  ['DB0106', 'Transparent Rose Luster', '#d47c9b', 'Luster'],
  ['DB0073', 'Lined Crystal Fuchsia', '#c2418a', 'Lined'],
  ['DB0776', 'Matte Transparent Magenta', '#a82f72', 'Matte transparent'],
  ['DB0779', 'Dyed Matte Fuchsia', '#b32a75', 'Matte'],

  // Purples
  ['DB0661', 'Opaque Purple', '#5d3b8e', 'Opaque'],
  ['DB0783', 'Matte Transparent Purple', '#6a4a94', 'Matte transparent'],
  ['DB0249', 'Lavender Ceylon', '#d6cee6', 'Ceylon'],
  ['DB0080', 'Lined Crystal Lilac', '#b7a4d6', 'Lined'],
  ['DB0134', 'Transparent Amethyst', '#7d5aa6', 'Transparent'],
  ['DB0029', 'Metallic Purple Iris', '#4d3b63', 'Metallic'],
  ['DB0873', 'Matte Transparent Grape', '#54387a', 'Matte transparent'],

  // Blues
  ['DB0216', 'Light Blue Ceylon', '#cfe0ee', 'Ceylon'],
  ['DB0755', 'Matte Opaque Light Blue', '#a9c6dd', 'Matte'],
  ['DB0164', 'Opaque Turquoise AB', '#2fa9b8', 'AB'],
  ['DB0658', 'Opaque Turquoise', '#1f9aa8', 'Opaque'],
  ['DB0759', 'Matte Opaque Turquoise', '#2f9aa2', 'Matte'],
  ['DB0725', 'Opaque Medium Blue', '#2f6fb5', 'Opaque'],
  ['DB0726', 'Opaque Blue', '#22539a', 'Opaque'],
  ['DB0756', 'Matte Opaque Cobalt', '#27508f', 'Matte'],
  ['DB0165', 'Opaque Navy AB', '#1d3268', 'AB'],
  ['DB0002', 'Metallic Dark Blue', '#22355c', 'Metallic'],
  ['DB0361', 'Matte Opaque Navy', '#22304f', 'Matte'],
  ['DB0714', 'Transparent Sapphire', '#2a4f8f', 'Transparent'],
  ['DB0693', 'Semi-Matte Silver Lined Blue', '#3a76b8', 'Silver-lined'],
  ['DB0057', 'Lined Crystal Aqua AB', '#8fd3dd', 'Lined AB'],
  ['DB0079', 'Lined Crystal Sky', '#a9cfe8', 'Lined'],

  // Greens
  ['DB0724', 'Opaque Green', '#2e8b45', 'Opaque'],
  ['DB0754', 'Matte Opaque Green', '#3a8a4d', 'Matte'],
  ['DB0663', 'Opaque Kelly Green', '#1f7a3d', 'Opaque'],
  ['DB0776g', 'Transparent Emerald', '#1d6b4c', 'Transparent'],
  ['DB0797', 'Matte Transparent Olive', '#6b6f33', 'Matte transparent'],
  ['DB0656', 'Opaque Sea Green', '#4f9b86', 'Opaque'],
  ['DB0877', 'Matte Opaque Mint', '#a9d3bd', 'Matte'],
  ['DB0237', 'Lined Crystal Light Green', '#bfe0be', 'Lined'],
  ['DB0655', 'Opaque Chartreuse', '#a6c33a', 'Opaque'],
  ['DB0733g', 'Opaque Forest', '#28513a', 'Opaque'],
  ['DB0027', 'Metallic Green Iris', '#3c5646', 'Metallic'],

  // Browns / earth
  ['DB0734', 'Opaque Chocolate', '#5a3a2b', 'Opaque'],
  ['DB0764', 'Matte Opaque Brown', '#6b4634', 'Matte'],
  ['DB0208', 'Opaque Light Tan', '#d9b892', 'Opaque'],
  ['DB0389', 'Matte Opaque Beige', '#c9ab89', 'Matte'],
  ['DB0794', 'Matte Transparent Root Beer', '#7a4f30', 'Matte transparent'],
  ['DB0653', 'Opaque Terracotta', '#b5583c', 'Opaque'],
];

const TOHO_ROWS: Row[] = [
  ['T-49', 'Opaque Jet', '#171717', 'Opaque'],
  ['T-41', 'Opaque White', '#f6f3ea', 'Opaque'],
  ['T-121', 'Opaque Lustered White', '#eee7d8', 'Luster'],
  ['T-45', 'Opaque Light Grey', '#b5b5b2', 'Opaque'],
  ['T-81', 'Metallic Hematite', '#4a4d52', 'Metallic'],
  ['T-22', 'Silver Lined Crystal', '#e7eef2', 'Silver-lined'],
  ['T-42', 'Opaque Cream', '#f2e4c4', 'Opaque'],
  ['T-42B', 'Opaque Butter', '#f1d267', 'Opaque'],
  ['T-30', 'Silver Lined Lemon', '#eed041', 'Silver-lined'],
  ['T-10', 'Transparent Orange', '#e8802c', 'Transparent'],
  ['T-45A', 'Opaque Pumpkin', '#dd7128', 'Opaque'],
  ['T-45F', 'Matte Opaque Red', '#c03a33', 'Matte'],
  ['T-25', 'Silver Lined Ruby', '#b32b40', 'Silver-lined'],
  ['T-38', 'Silver Lined Rose', '#e07a99', 'Silver-lined'],
  ['T-2107', 'Opaque Pink Blush', '#efc0c9', 'Opaque'],
  ['T-19', 'Transparent Amethyst', '#7a55a3', 'Transparent'],
  ['T-48', 'Opaque Purple', '#5b3a8c', 'Opaque'],
  ['T-43', 'Opaque Light Blue', '#a7c8e0', 'Opaque'],
  ['T-8', 'Transparent Cobalt', '#26529b', 'Transparent'],
  ['T-48D', 'Opaque Navy', '#22305a', 'Opaque'],
  ['T-55', 'Opaque Turquoise', '#1f9fae', 'Opaque'],
  ['T-7BD', 'Transparent Teal', '#1f6f72', 'Transparent'],
  ['T-47', 'Opaque Mint Green', '#9ecfae', 'Opaque'],
  ['T-47H', 'Opaque Kelly Green', '#2b8248', 'Opaque'],
  ['T-940', 'Transparent Olive', '#6e7135', 'Transparent'],
  ['T-46', 'Opaque Terracotta', '#b0573d', 'Opaque'],
  ['T-46L', 'Opaque Chocolate', '#5c3b2c', 'Opaque'],
  ['T-123', 'Gold Lustered Beige', '#d8bb90', 'Luster'],
  ['T-712', 'Metallic Gold', '#c8a24d', 'Metallic'],
  ['T-711', 'Metallic Copper', '#a9663f', 'Metallic'],
];

const BASIC_ROWS: Row[] = [
  ['', 'Black', '#000000'],
  ['', 'Charcoal', '#333333'],
  ['', 'Dark Grey', '#555555'],
  ['', 'Grey', '#808080'],
  ['', 'Light Grey', '#bbbbbb'],
  ['', 'Silver', '#dddddd'],
  ['', 'White', '#ffffff'],
  ['', 'Ivory', '#f5eddc'],
  ['', 'Sand', '#e0cba8'],
  ['', 'Tan', '#c8a173'],
  ['', 'Brown', '#8b5a2b'],
  ['', 'Dark Brown', '#4b2e1a'],
  ['', 'Maroon', '#7b1e2b'],
  ['', 'Red', '#e02020'],
  ['', 'Coral', '#f4634f'],
  ['', 'Orange', '#f28020'],
  ['', 'Amber', '#f5a623'],
  ['', 'Gold', '#d4af37'],
  ['', 'Yellow', '#f7e017'],
  ['', 'Lemon', '#fbf089'],
  ['', 'Lime', '#b5e61d'],
  ['', 'Chartreuse', '#7fc41b'],
  ['', 'Green', '#22a34a'],
  ['', 'Forest', '#14683a'],
  ['', 'Emerald', '#0f9b6c'],
  ['', 'Mint', '#a8e6c4'],
  ['', 'Teal', '#158f8f'],
  ['', 'Turquoise', '#20c0c8'],
  ['', 'Sky', '#7ec8f0'],
  ['', 'Cyan', '#22c3e6'],
  ['', 'Azure', '#2f80ed'],
  ['', 'Blue', '#1f4fd8'],
  ['', 'Navy', '#16255c'],
  ['', 'Periwinkle', '#9aa8f0'],
  ['', 'Violet', '#7b4fd8'],
  ['', 'Purple', '#5b2d90'],
  ['', 'Plum', '#7a2f6a'],
  ['', 'Orchid', '#c07fd0'],
  ['', 'Lavender', '#d6c8f0'],
  ['', 'Magenta', '#d81b8c'],
  ['', 'Fuchsia', '#f050b0'],
  ['', 'Rose', '#f08fae'],
  ['', 'Blush', '#f7c9cf'],
  ['', 'Peach', '#f9c9a0'],
  ['', 'Olive', '#6b7027'],
  ['', 'Khaki', '#a8a05e'],
  ['', 'Slate', '#4a5a6a'],
  ['', 'Steel', '#7c8b99'],
];

const GREYSCALE_ROWS: Row[] = Array.from({ length: 12 }, (_, i) => {
  const v = Math.round((i / 11) * 255);
  const h = v.toString(16).padStart(2, '0');
  return ['', `Grey ${i}`, `#${h}${h}${h}`] as Row;
});

export const LIBRARIES: BeadLibrary[] = [
  {
    id: 'delica11',
    label: 'Miyuki Delica 11/0',
    brand: 'Miyuki',
    note: 'Approximate screen colours — check a physical colour card before ordering.',
    colors: build('Miyuki', 'db', DELICA_ROWS),
  },
  {
    id: 'toho',
    label: 'Toho Round',
    brand: 'Toho',
    note: 'Approximate screen colours — check a physical colour card before ordering.',
    colors: build('Toho', 'th', TOHO_ROWS),
  },
  { id: 'basics', label: 'Basic colours', brand: '', colors: build('', 'bc', BASIC_ROWS) },
  { id: 'greys', label: 'Greyscale', brand: '', colors: build('', 'gs', GREYSCALE_ROWS) },
];

export function libraryById(id: string): BeadLibrary | undefined {
  return LIBRARIES.find((l) => l.id === id);
}

let customSeq = 0;
export function customColor(hex: string, name?: string): BeadColor {
  customSeq += 1;
  return {
    id: `custom-${Date.now().toString(36)}-${customSeq}`,
    code: '',
    name: name ?? hex.toUpperCase(),
    hex,
    brand: 'Custom',
  };
}

/** Parse a `code,name,hex,finish` CSV into bead colours. Tolerates a header. */
export function parsePaletteCsv(text: string, brand = 'Imported'): BeadColor[] {
  const out: BeadColor[] = [];
  const lines = text.split(/\r?\n/);
  for (const [i, raw] of lines.entries()) {
    const line = raw.trim();
    if (!line) continue;
    const parts = line.split(',').map((p) => p.trim().replace(/^"|"$/g, ''));
    const hexPart = parts.find((p) => /^#?[0-9a-f]{6}$/i.test(p));
    if (!hexPart) continue; // skips headers and junk lines
    const hex = hexPart.startsWith('#') ? hexPart.toLowerCase() : `#${hexPart.toLowerCase()}`;
    const rest = parts.filter((p) => p !== hexPart);
    const code = rest[0] ?? '';
    const name = rest[1] ?? code ?? hex;
    out.push({
      id: `imp-${code || hex}-${i}`,
      code,
      name,
      hex,
      brand,
      finish: rest[2],
    });
  }
  return out;
}

export function paletteToCsv(colors: BeadColor[]): string {
  const rows = ['code,name,hex,finish'];
  for (const c of colors) {
    if (c.id === 'empty') continue;
    rows.push([c.code, c.name, c.hex, c.finish ?? ''].map((v) => `"${v}"`).join(','));
  }
  return rows.join('\n');
}
